import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { Store } from '../../../services/store';
import { SessionStore } from '../../../services/session.store';
import { Point } from '../../../models/point';
import { toVoid } from '../../../utils/void';
import { SettingsUsersProvider } from './settings-users.provider';
import { InviteMemberRequestDto, MemberRole, PasswordLinkPurpose } from './models/member.dto';
import { MemberRowVm } from './models/member-row.view-model';

// The one-time link, shown once: closing the panel is the only way it leaves memory (0058)
export interface LinkPanel {
  displayName: string;
  linkUrl: string;
  purpose: PasswordLinkPurpose;
}

export interface RowMenu {
  memberId: number;
  position: Point;
}

export interface SettingsUsersState {
  loading: boolean;
  members: MemberRowVm[] | undefined;
  inviteOpen: boolean;
  inviting: boolean;
  inviteError: string | undefined;
  link: LinkPanel | undefined;
  rowMenu: RowMenu | null;
  actionError: string | undefined;
}

const INITIAL_STATE: SettingsUsersState = {
  loading: false,
  members: undefined,
  inviteOpen: false,
  inviting: false,
  inviteError: undefined,
  link: undefined,
  rowMenu: null,
  actionError: undefined
};

@Injectable({ providedIn: 'root' })
export class SettingsUsersStore extends Store<SettingsUsersState> {
  constructor(
    private provider: SettingsUsersProvider,
    private sessionStore: SessionStore
  ) {
    super(INITIAL_STATE);
  }

  init(): Observable<void> {
    this.update(state => ({ ...state, loading: true }));

    return this.load();
  }

  // --- Zaproś użytkownika ---

  openInvite(): void {
    this.update(state => ({ ...state, inviteOpen: true, inviteError: undefined }));
  }

  closeInvite(): void {
    this.update(state => ({ ...state, inviteOpen: false, inviteError: undefined }));
  }

  invite(request: InviteMemberRequestDto): Observable<void> {
    this.update(state => ({ ...state, inviting: true, inviteError: undefined }));

    return this.provider.invite(request).pipe(
      tap(invited => this.update(state => ({
        ...state,
        inviting: false,
        inviteOpen: false,
        link: { displayName: invited.member.displayName, linkUrl: invited.linkUrl, purpose: 'Invite' }
      }))),
      switchMap(() => this.load()),
      catchError(error => {
        this.update(state => ({ ...state, inviting: false, inviteError: apiMessage(error, 'Nie udało się zaprosić użytkownika') }));
        return of(undefined);
      })
    );
  }

  closeLink(): void {
    this.update(state => ({ ...state, link: undefined }));
  }

  // --- row menu ---

  openRowMenu(memberId: number, position: Point): void {
    this.update(state => ({ ...state, rowMenu: { memberId, position } }));
  }

  closeRowMenu(): void {
    this.update(state => ({ ...state, rowMenu: null }));
  }

  // --- row actions: each one round-trips and reloads the table, so the row shows what the API holds ---

  rename(memberId: number, displayName: string): Observable<void> {
    return this.act(this.provider.patch(memberId, { displayName }), 'Nie udało się zmienić nazwy');
  }

  changeRole(memberId: number, role: MemberRole): Observable<void> {
    return this.act(this.provider.patch(memberId, { role }), 'Nie udało się zmienić roli');
  }

  setAdmin(memberId: number, isAdmin: boolean): Observable<void> {
    return this.act(this.provider.patch(memberId, { isAdmin }), 'Nie udało się zmienić uprawnień');
  }

  deactivate(memberId: number): Observable<void> {
    return this.act(this.provider.deactivate(memberId), 'Nie udało się dezaktywować użytkownika');
  }

  activate(memberId: number): Observable<void> {
    return this.act(this.provider.activate(memberId), 'Nie udało się aktywować użytkownika');
  }

  unlock(memberId: number): Observable<void> {
    return this.act(this.provider.unlock(memberId), 'Nie udało się odblokować konta');
  }

  issuePasswordLink(memberId: number): Observable<void> {
    const member = this.state.members?.find(x => x.id === memberId);

    return this.act(
      this.provider.issuePasswordLink(memberId).pipe(
        tap(created => this.update(state => ({
          ...state,
          link: { displayName: member?.displayName ?? '', linkUrl: created.linkUrl, purpose: created.purpose }
        })))
      ),
      'Nie udało się wygenerować linku');
  }

  private act(request: Observable<unknown>, fallback: string): Observable<void> {
    this.update(state => ({ ...state, actionError: undefined }));

    return request.pipe(
      switchMap(() => this.load()),
      catchError(error => {
        this.update(state => ({ ...state, actionError: apiMessage(error, fallback) }));
        return of(undefined);
      })
    );
  }

  private load(): Observable<void> {
    const currentEmail = this.sessionStore.state.session?.email ?? '';

    return this.provider.getMembers().pipe(
      tap(members => this.update(state => ({
        ...state,
        loading: false,
        members: MemberRowVm.mapListToViewModels(members, currentEmail)
      }))),
      toVoid()
    );
  }
}

// The API answers a refused action with a sentence (400); anything else gets the fallback
function apiMessage(error: { error?: unknown }, fallback: string): string {
  return typeof error.error === 'string' ? error.error : fallback;
}
